import { AffectedScope } from "../../../../../customer-experience/src/v2/src/features/recommendations-feature/utils/recommendationTypes";
import { AffectedResourceDto } from "../../../../../customer-experience/src/lib/ShiOneClient";

interface ParsedResource {
  resourceType: string;
  resourceName: string;
  resourceId: string;
  accountId?: string;
}

const parseResourceIdentifier = (resourceId: string): ParsedResource => {
  // Case 1: Special AWS::::Account format
  if (resourceId.startsWith("AWS::::Account:")) {
    const accountId = resourceId.split(":").pop() || resourceId;
    return {
      resourceType: "AWS Account",
      resourceName: `Account ${accountId}`,
      resourceId,
      accountId,
    };
  }

  // Case 2 & 3: Standard ARN format
  // Format: arn:partition:service:region:account-id:resource-type/resource-id
  const arnPattern = /^arn:([^:]*):([^:]*):([^:]*):([^:]*):(.*)$/;
  const match = resourceId.match(arnPattern);

  if (match) {
    const [, partition, service, region, accountId, resourcePart] = match;

    // Parse resource part (could be type/id, type:id, or just id)
    let resourceType = service;
    let resourceName = resourcePart;

    if (resourcePart.includes("/")) {
      const [type, ...nameParts] = resourcePart.split("/");
      resourceType = type || service;
      resourceName = nameParts.join("/") || resourcePart;
    } else if (resourcePart.includes(":")) {
      const [type, ...nameParts] = resourcePart.split(":");
      resourceType = type || service;
      resourceName = nameParts.join(":") || resourcePart;
    }

    return {
      resourceType:
        resourceType.charAt(0).toUpperCase() + resourceType.slice(1),
      resourceName,
      resourceId,
      accountId: accountId || undefined,
    };
  }

  return {
    resourceType: "Resource",
    resourceName: resourceId,
    resourceId,
  };
};

export const parseArnToListItemResource = (arn: string) => {
  const arnParts = arn.split(":");
  if (arnParts.length >= 6) {
    const resourcePart = arnParts.slice(5).join(":");
    const [resourceType, ...resourceIdParts] = resourcePart.split("/");
    return {
      resourceType: resourceType || "Instance",
      resourceName: resourceIdParts.join("/") || arn.split("/").pop() || arn,
      resourceId: arn,
    };
  }
  return { resourceType: "Resource", resourceName: arn, resourceId: arn };
};

export const transformSecurityDataToScopes = (
  securityMetadata: any,
): AffectedScope[] => {
  if (!securityMetadata) return [];

  const {
    accounts = [],
    resourceIds = [],
    resourceTypes = [],
  } = securityMetadata;
  const defaultResourceType = resourceTypes[0] || "Resource";

  const parsedResources = resourceIds.map((id: string) =>
    parseResourceIdentifier(id),
  );

  const accountResourceMap = new Map<string, AffectedResourceDto[]>();

  parsedResources.forEach((resource: ParsedResource) => {
    let targetAccount = resource.accountId;

    // If resource doesn't have an account ID and we have exactly one account, use it
    if (!targetAccount && accounts.length === 1) {
      targetAccount = accounts[0];
    }

    // If still no account, check if this is an account-level resource
    if (!targetAccount && resource.resourceType === "AwsAccount") {
      targetAccount =
        resource.accountId || resource.resourceName.replace("Account ", "");
    }

    // Default to 'shared' for resources without clear account ownership when multiple accounts exist
    if (!targetAccount && accounts.length > 1) {
      targetAccount = "shared-resources";
    }

    targetAccount = targetAccount || "unknown";

    const existingResources = accountResourceMap.get(targetAccount) || [];
    accountResourceMap.set(targetAccount, [
      ...existingResources,
      {
        resourceId: resource.resourceId,
        resourceName: resource.resourceName,
        resourceType:
          defaultResourceType === "Resource"
            ? resource.resourceType
            : defaultResourceType,
      } as AffectedResourceDto,
    ]);
  });

  accounts.forEach((accountId: string) => {
    if (!accountResourceMap.has(accountId)) {
      accountResourceMap.set(accountId, []);
    }
  });

  return Array.from(accountResourceMap.entries())
    .sort(([a], [b]) => {
      if (a === "shared-resources" || a === "unknown") return 1;
      if (b === "shared-resources" || b === "unknown") return -1;
      return a.localeCompare(b);
    })
    .map(([scopeId, resources]) => ({
      scopeId,
      affectedResources: resources,
    }));
};
